/* eslint-disable react/require-default-props */
import {
  Popover,
  PopoverTrigger,
  Button,
  Portal,
  PopoverContent,
  PopoverBody,
  PopoverFooter,
  ButtonGroup,
  useDisclosure,
  Text,
  Box,
} from '@chakra-ui/core';
import React, { FC, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidV4 } from 'uuid';

interface DeleteFormElementProps {
  popoverTrigger: React.ReactNode;
  onYes: () => void;
  open?: boolean;
  handleClose?: () => void;
  popoverBodyText?: string;
}

const DeleteFormElement: FC<DeleteFormElementProps> = React.memo(
  function DeleteFormElementrComponent({ popoverTrigger, onYes, open, handleClose, popoverBodyText }: DeleteFormElementProps) {
    const { isOpen, onOpen, onClose: internalOnClose } = useDisclosure();
    const { t } = useTranslation('hrFormTemplate');
    const id = uuidV4();

    useEffect(() => {
      if (open === true && !isOpen) {
        onOpen();
      } else if (open === false && isOpen) {
        internalOnClose();
      }
    }, [open, isOpen, onOpen, internalOnClose]);

    const effectiveOnClose = useCallback(() => {
      internalOnClose();
      if (handleClose) {
        handleClose();
      }
    }, [internalOnClose, handleClose]);

    const handleConfirm = useCallback(() => {
      effectiveOnClose();
      onYes();
    }, [effectiveOnClose, onYes]);

    return (
      <Box position="relative">
        <Popover
          isOpen={isOpen}
          onOpen={onOpen}
          onClose={effectiveOnClose}
          closeOnBlur
          closeOnEsc
          autoFocus={false}
          placement="left-start"
          isLazy
        >
          <PopoverTrigger>{popoverTrigger}</PopoverTrigger>
          <Portal>
            <PopoverContent data-testid="delete-popover-content" boxShadow="lg" borderRadius="md" key={id}>
              <PopoverBody py={4} px={5}>
                <Text fontSize="sm">
                  {popoverBodyText || t('formBuilder.deletePopoverMessage') || 'Are you sure you want to delete this?'}
                </Text>
              </PopoverBody>
              <PopoverFooter display="flex" alignItems="center" justifyContent="flex-end" borderTopWidth="1px" p={3}>
                <ButtonGroup size="sm">
                  <Button variant="outline" onClick={effectiveOnClose} data-testid="delete-popover-no-button">
                    {t('formBuilder.no', 'No')}
                  </Button>
                  <Button colorScheme="red" onClick={handleConfirm} data-testid="delete-popover-yes-button">
                    {t('formBuilder.yes', 'Yes')}
                  </Button>
                </ButtonGroup>
              </PopoverFooter>
            </PopoverContent>
          </Portal>
        </Popover>
      </Box>
    );
  },
  (p, n) =>
    // eslint-disable-next-line
    p.popoverTrigger === n.popoverTrigger &&
    p.onYes === n.onYes &&
    p.open === n.open &&
    p.handleClose === n.handleClose &&
    p.popoverBodyText === n.popoverBodyText,
);

export default DeleteFormElement;
